import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  generateBusinessOpportunity,
  generateBusinessOpportunityErrorMessage,
} from '../lib/generateBusinessOpportunity';
import { useUser } from './UserContext';

type OpportunityDiscoveryContextValue = {
  discovering: boolean;
  error: string | null;
  startDiscovery: () => Promise<void>;
  dismissError: () => void;
};

const OpportunityDiscoveryContext = createContext<OpportunityDiscoveryContextValue | undefined>(
  undefined
);

export function OpportunityDiscoveryProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { user } = useUser();
  const userId = user?.uid ?? '';
  const [discovering, setDiscovering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const runningRef = useRef(false);

  const startDiscovery = useCallback(async () => {
    if (runningRef.current) {
      return;
    }
    if (!userId) {
      setError('Sign in to discover opportunities.');
      return;
    }

    runningRef.current = true;
    setDiscovering(true);
    setError(null);

    try {
      const discovered = await generateBusinessOpportunity();
      navigate(`/opportunities/${discovered.id}?tab=analysis`);
    } catch (err) {
      setError(generateBusinessOpportunityErrorMessage(err));
    } finally {
      runningRef.current = false;
      setDiscovering(false);
    }
  }, [navigate, userId]);

  const dismissError = useCallback(() => {
    setError(null);
  }, []);

  const value = useMemo(
    () => ({
      discovering,
      error,
      startDiscovery,
      dismissError,
    }),
    [discovering, error, startDiscovery, dismissError]
  );

  return (
    <OpportunityDiscoveryContext.Provider value={value}>
      {children}
    </OpportunityDiscoveryContext.Provider>
  );
}

export function useOpportunityDiscovery(): OpportunityDiscoveryContextValue {
  const ctx = useContext(OpportunityDiscoveryContext);
  if (!ctx) {
    throw new Error('useOpportunityDiscovery must be used within OpportunityDiscoveryProvider');
  }
  return ctx;
}
